import { useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFragment, graphql } from 'react-relay';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import { trackEvent } from 'dibs-tracking';
import { ItemTileCarousel } from 'dibs-search-product-tile/exports/ItemTile';
import { useSubscriptionSkippable } from 'dibs-subscriptions-utils/exports/useSubscriptionSkippable';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider';
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';

import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';
import { SRC_SET_SIZES_TILE } from '../../constants/imageConstants';

import styles from './SharedItemTile.scss';

const itemSearchFragment = graphql`
    fragment SharedItemTile_itemSearch on ItemSearchQueryConnection {
        ...ItemTileCarousel_itemSearch
        pageType
    }
`;
const itemFragment = graphql`
    fragment SharedItemTile_item on Item
    @argumentDefinitions(
        photosLimit: { type: "Int", defaultValue: 1 }
        fetchVideo: { type: "Boolean", defaultValue: false }
        pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
        userCountryCode: { type: "String", defaultValue: "" }
        priceBookName: { type: "String" }
        showSeller: { type: "Boolean!" }
        isTrade: { type: "Boolean" }
    ) {
        ...ItemTileCarousel_item
            @arguments(
                showSeller: $showSeller
                isTrade: $isTrade
                fetchVideo: $fetchVideo
                pageDisplayEnum: $pageDisplayEnum
                userCountryCode: $userCountryCode
                priceBookName: $priceBookName
            )
        ...FavoritesItem_item
        ...FavoritesItemProvider_item
        isAuctionEnabled
        serviceId
        productPhotos: photos(limit: $photosLimit) {
            smallPath
            versions {
                webPath
            }
        }
        isNewListing
    }
`;

const userFragment = graphql`
    fragment SharedItemTile_user on User {
        ...FavoritesItemProvider_user
    }
`;

const viewerFragment = graphql`
    fragment SharedItemTile_viewer on Viewer {
        ...ItemTileCarousel_viewer
            @arguments(
                fetchRegionalInfo: $fetchRegionalInfo
                userZipCode: $userZipCode
                userCountryCode: $userCountryCode
            )
    }
`;

export function SharedItemTile(props) {
    const {
        isSponsored,
        favorites,
        index,
        item: staticItemRef,
        user: userRef,
        isClient,
        isVisuallySimilarEnabled,
        isMobile,
        lazyLoadStartIndex,
        onClick,
        onContentLoaded,
        showInternalSellerName,
        showSellerName,
        showCreatorName,
        showMeasurements,
        showStorefrontOnlyBadge,
        measurementUnit,
        quickView,
        imageLoadVerticalOffset,
        onContentVisible,
        customClassName,
        imageSize,
        viewer: viewerRef,
        itemSearch: itemSearchRef,
        tileType,
        showSwipeIndicator,
        fetchFolderOnMouseOver,
        showFavoritesTooltip,
        displayOriginalListPrice,
        additionalElements,
        showPriceContainer = true,
        showTitle,
        showQuickView,
        showMSKUSwatch,
        showShippingBadge,
        showNoReserveBadge,
        showItemLocationBadge = false,
    } = props;

    const [itemRef, setItemRef] = useState(staticItemRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const tileElementRef = useRef(null);
    const { activeQuickView, closeQuickView, openQuickView } = quickView || {};
    const itemId = item?.serviceId || '';
    const isAuctionsSbPage = itemSearch?.pageType === 'auction';
    const variables = useSelector(state => state.relayVariables.variables);
    const { showSeller, isTrade, userCountryCode, priceBookName } = variables || {};

    const shouldSkipSubscription = !(isAuctionsSbPage && item?.isAuctionEnabled);
    const subscriptionConfig = useMemo(
        () => ({
            subscription: graphql`
                subscription SharedItemTileSubscription(
                    $input: AuctionItemUpdateSubscriptionInput!
                    $isTrade: Boolean
                    $showSeller: Boolean = false # $pageDisplayEnum: PageDisplayEnum
                    $userCountryCode: String = ""
                    $priceBookName: String
                ) {
                    auctionItemUpdate(input: $input) {
                        item {
                            ...SharedItemTile_item
                                @arguments(
                                    isTrade: $isTrade
                                    userCountryCode: $userCountryCode
                                    priceBookName: $priceBookName
                                    showSeller: $showSeller
                                )
                        }
                    }
                }
            `,
            variables: {
                input: {
                    itemId,
                },
                showSeller,
                isTrade,
                pageDisplayEnum: 'auctionsSearchAndBrowse',
                userCountryCode,
                priceBookName,
            },
            onNext: response => {
                const ref = response?.auctionItemUpdate?.item;
                if (ref) {
                    setItemRef(ref);
                }
            },
        }),
        [itemId, showSeller, isTrade, userCountryCode, priceBookName]
    );

    useSubscriptionSkippable(subscriptionConfig, shouldSkipSubscription);

    function onQuickViewDetailsClick() {
        trackEvent({
            category: 'ecommerce',
            label: itemId,
            action: 'product detail view - from seo quicklook',
        });
    }

    return (
        <CurrencyProvider
            render={({ currency, showCurrency }) => {
                const sharedProps = {
                    item,
                    index,
                    imageLoadVerticalOffset,
                    onClick,
                    onContentLoaded,
                    imageSize,
                    showTitle,
                    showPriceContainer,
                    showQuickView,
                };
                const photo = item?.productPhotos?.[0];
                if (!photo?.smallPath && !photo?.versions?.[0]?.webPath) {
                    return null;
                }
                return (
                    <div
                        className={classnames(styles.tileWrapper, {
                            [styles.tileWidth]: !customClassName,
                            [customClassName]: !!customClassName,
                            [styles.completeInfo]: true,
                        })}
                        data-tn="item-tile-wrapper"
                        data-tile-index={index}
                        ref={tileElementRef}
                    >
                        {additionalElements}
                        <FavoritesItemProvider
                            item={item}
                            user={user}
                            applyMouseEvents={fetchFolderOnMouseOver}
                            className={styles.favoritesItemProvider}
                        >
                            {({ isFolderVisible, ...favoritesItemProps }) => {
                                const applyIconWrapperClass = isMobile || !fetchFolderOnMouseOver;
                                const favoritesComponent = favorites ? (
                                    <FavoritesItem
                                        isNewListing={item?.isNewListing || false}
                                        isSponsored={isSponsored}
                                        wrapperClass={styles.favoritesWrapper}
                                        heartIconWrapperClass={classnames(
                                            styles.heartIconWrapperSpacing,
                                            {
                                                [styles.reducedIcon]: applyIconWrapperClass,
                                            }
                                        )}
                                        folderIconWrapperClass={
                                            applyIconWrapperClass ? styles.reducedIcon : undefined
                                        }
                                        item={item}
                                        {...favorites}
                                        fetchFolder={
                                            favorites.fetchFolder &&
                                            (!fetchFolderOnMouseOver || isFolderVisible)
                                        }
                                        showTooltip={showFavoritesTooltip}
                                        {...favoritesItemProps}
                                    />
                                ) : null;

                                return (
                                    <ItemTileCarousel
                                        {...sharedProps}
                                        isSponsored={isSponsored}
                                        useLoFiLazyLoader
                                        itemsPerRow={lazyLoadStartIndex}
                                        activeQuickView={activeQuickView}
                                        isMobile={isMobile}
                                        onContentVisible={onContentVisible}
                                        openQuickView={openQuickView}
                                        closeQuickView={closeQuickView}
                                        srcSetSizes={SRC_SET_SIZES_TILE}
                                        showQuickViewIcon={!!isClient}
                                        showSellerName={showSellerName}
                                        showCreatorName={showCreatorName}
                                        showInternalSellerName={showInternalSellerName}
                                        showStorefrontOnlyBadge={showStorefrontOnlyBadge}
                                        currency={currency}
                                        showPrice={isClient && showCurrency}
                                        measurementUnit={measurementUnit}
                                        onQuickViewDetailsClick={onQuickViewDetailsClick}
                                        favoritesComponent={favoritesComponent}
                                        showMeasurements={showMeasurements}
                                        showBrowseSimilar={isVisuallySimilarEnabled && isClient}
                                        viewer={viewer}
                                        tileType={tileType}
                                        itemSearch={itemSearch}
                                        showSwipeIndicator={showSwipeIndicator}
                                        displayOriginalListPrice={displayOriginalListPrice}
                                        showMSKUSwatch={showMSKUSwatch}
                                        showItemLocationBadge={showItemLocationBadge}
                                        showShippingBadge={showShippingBadge}
                                        showNoReserveBadge={showNoReserveBadge}
                                    />
                                );
                            }}
                        </FavoritesItemProvider>
                    </div>
                );
            }}
        />
    );
}

SharedItemTile.defaultProps = {
    isSponsored: false,
    isMobile: true,
    lazyLoadStartIndex: 2,
    size: 'medium',
    shippingBadge: null,
    displayOriginalListPrice: false,
    additionalElements: null,
};

SharedItemTile.propTypes = {
    isSponsored: PropTypes.bool,
    favorites: PropTypes.object,
    quickView: PropTypes.shape({
        activeQuickView: PropTypes.number,
        closeQuickView: PropTypes.func,
        openQuickView: PropTypes.func,
    }),
    index: PropTypes.number.isRequired,
    isMobile: PropTypes.bool,
    isVisuallySimilarEnabled: PropTypes.bool,
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    lazyLoadStartIndex: PropTypes.number,
    imageLoadVerticalOffset: PropTypes.number,
    onClick: PropTypes.func,
    onContentLoaded: PropTypes.func,
    showInternalSellerName: PropTypes.bool,
    showSellerName: PropTypes.bool,
    showCreatorName: PropTypes.bool,
    showMeasurements: PropTypes.bool,
    showStorefrontOnlyBadge: PropTypes.bool,
    measurementUnit: PropTypes.string,
    isClient: PropTypes.bool.isRequired,
    onContentVisible: PropTypes.func,
    customClassName: PropTypes.string,
    imageSize: PropTypes.string,
    hideDivider: PropTypes.bool,
    viewer: PropTypes.object,
    itemSearch: PropTypes.object,
    tileType: PropTypes.string,
    showSwipeIndicator: PropTypes.bool,
    fetchFolderOnMouseOver: PropTypes.bool,
    showFavoritesTooltip: PropTypes.bool,
    displayOriginalListPrice: PropTypes.bool,
    additionalElements: PropTypes.node,
    showPriceContainer: PropTypes.bool,
    showTitle: PropTypes.bool,
    showQuickView: PropTypes.bool,
    showMSKUSwatch: PropTypes.bool,
    showShippingBadge: PropTypes.bool,
    showNoReserveBadge: PropTypes.bool,
    showItemLocationBadge: PropTypes.bool,
};
